// Shared status/priority presentation for Service Desk — single source of truth
// for labels (Title Case), colors and badge chips so the agent queue, ticket
// detail and customer portal all look identical and enterprise-grade.
import React from 'react';

export const STATUS_OPTIONS = ['open', 'in_progress', 'pending', 'resolved', 'closed'];
export const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];

const cap = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const statusLabel = (s) =>
  ({ open: 'Open', in_progress: 'In Progress', pending: 'Pending', resolved: 'Resolved', closed: 'Closed' }[s] || cap(s));
export const priorityLabel = (p) =>
  ({ low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' }[p] || cap(p));

export const STATUS_DOT = {
  open: 'bg-blue-500', in_progress: 'bg-amber-500', pending: 'bg-zinc-400',
  resolved: 'bg-emerald-500', closed: 'bg-zinc-400',
};
export const PRIORITY_DOT = {
  low: 'bg-zinc-400', medium: 'bg-amber-500', high: 'bg-orange-500', urgent: 'bg-red-500',
};

const STATUS_TONE = {
  open: 'bg-blue-50 text-blue-700 ring-blue-200',
  in_progress: 'bg-amber-50 text-amber-700 ring-amber-200',
  pending: 'bg-zinc-100 text-zinc-600 ring-zinc-200',
  resolved: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  closed: 'bg-zinc-100 text-zinc-500 ring-zinc-200',
};
const PRIORITY_TONE = {
  low: 'bg-zinc-100 text-zinc-600 ring-zinc-200',
  medium: 'bg-amber-50 text-amber-700 ring-amber-200',
  high: 'bg-orange-50 text-orange-700 ring-orange-200',
  urgent: 'bg-red-50 text-red-700 ring-red-200',
};

export const Dot = ({ className = '' }) => <span className={`w-2 h-2 rounded-full shrink-0 ${className}`} />;

export function StatusBadge({ status, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium ring-1 ${STATUS_TONE[status] || STATUS_TONE.open} ${className}`}>
      <Dot className={`!w-1.5 !h-1.5 ${STATUS_DOT[status] || 'bg-zinc-400'}`} />
      {statusLabel(status)}
    </span>
  );
}

export function PriorityBadge({ priority, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium ring-1 ${PRIORITY_TONE[priority] || PRIORITY_TONE.low} ${className}`}>
      <Dot className={`!w-1.5 !h-1.5 ${PRIORITY_DOT[priority] || 'bg-zinc-400'}`} />
      {priorityLabel(priority)}
    </span>
  );
}

// ───────────────────────── CATEGORY (Jira issue-type model) ─────────────────────────
export const CATEGORY_OPTIONS = ['service_request', 'incident', 'problem', 'change'];
export const categoryLabel = (c) =>
  ({ service_request: 'Service Request', incident: 'Incident', problem: 'Problem', change: 'Change' }[c] || cap(c));

import { Headset, Warning, Bug, ArrowsClockwise } from '@phosphor-icons/react';
export const CATEGORY_META = {
  service_request: { icon: Headset, tone: 'bg-blue-50 text-blue-700 ring-blue-200', dot: 'text-blue-600' },
  incident: { icon: Warning, tone: 'bg-red-50 text-red-700 ring-red-200', dot: 'text-red-600' },
  problem: { icon: Bug, tone: 'bg-purple-50 text-purple-700 ring-purple-200', dot: 'text-purple-600' },
  change: { icon: ArrowsClockwise, tone: 'bg-amber-50 text-amber-700 ring-amber-200', dot: 'text-amber-600' },
};

export function CategoryBadge({ category = 'service_request', className = '' }) {
  const meta = CATEGORY_META[category] || CATEGORY_META.service_request;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium ring-1 ${meta.tone} ${className}`}>
      <Icon size={12} weight="fill" /> {categoryLabel(category)}
    </span>
  );
}
